import { seoConfig } from '../config/seoConfig';

const SITE_URL = seoConfig.siteUrl;
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

const absoluteUrl = (pathOrUrl: string): string => {
  if (!pathOrUrl) return SITE_URL;
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
};

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'Behandlung.de',
  url: SITE_URL,
  logo: absoluteUrl(seoConfig.defaultOgImage),
  sameAs: [] as string[],
});

export const webSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: SITE_URL,
  name: 'Behandlung.de',
  publisher: { '@id': ORG_ID },
  inLanguage: 'de-DE',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export const siteWideSchemas = () => [organizationSchema(), webSiteSchema()];

export type BreadcrumbItem = { name: string; url: string };

export const breadcrumbSchema = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: item.name,
    item: absoluteUrl(item.url),
  })),
});

export type ArticleInput = {
  headline: string;
  description?: string;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
};

export const articleSchema = (input: ArticleInput) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: input.headline,
  description: input.description,
  mainEntityOfPage: { '@type': 'WebPage', '@id': absoluteUrl(input.url) },
  url: absoluteUrl(input.url),
  image: input.image ? absoluteUrl(input.image) : undefined,
  datePublished: input.datePublished,
  dateModified: input.dateModified || input.datePublished,
  author: input.authorName
    ? { '@type': 'Person', name: input.authorName }
    : { '@id': ORG_ID },
  publisher: { '@id': ORG_ID },
});

export type MedicalWebPageInput = {
  name: string;
  description?: string;
  url: string;
  about?: { name: string; type?: 'MedicalCondition' | 'MedicalProcedure' | 'MedicalTherapy' };
  lastReviewed?: string;
  breadcrumb?: BreadcrumbItem[];
};

export const medicalWebPageSchema = (input: MedicalWebPageInput) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: input.name,
    description: input.description,
    url: absoluteUrl(input.url),
    inLanguage: 'de-DE',
    publisher: { '@id': ORG_ID },
    isPartOf: { '@id': WEBSITE_ID },
  };
  if (input.about) {
    schema.about = {
      '@type': input.about.type || 'MedicalCondition',
      name: input.about.name,
    };
  }
  if (input.lastReviewed) {
    schema.lastReviewed = input.lastReviewed;
  }
  if (input.breadcrumb && input.breadcrumb.length > 0) {
    schema.breadcrumb = breadcrumbSchema(input.breadcrumb);
  }
  return schema;
};

export type FAQItem = { question: string; answer: string };

export const faqPageSchema = (items: FAQItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
});

export type PhysicianInput = {
  name: string;
  url: string;
  image?: string;
  description?: string;
  medicalSpecialty?: string;
  telephone?: string;
  email?: string;
  address?: {
    streetAddress?: string;
    postalCode?: string;
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
  };
  geo?: { latitude: number; longitude: number };
  aggregateRating?: { ratingValue: number; reviewCount: number };
};

export const physicianSchema = (input: PhysicianInput) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Physician',
    name: input.name,
    url: absoluteUrl(input.url),
    description: input.description,
    image: input.image ? absoluteUrl(input.image) : undefined,
    medicalSpecialty: input.medicalSpecialty,
    telephone: input.telephone,
    email: input.email,
  };
  if (input.address) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: input.address.streetAddress,
      postalCode: input.address.postalCode,
      addressLocality: input.address.addressLocality,
      addressRegion: input.address.addressRegion,
      addressCountry: input.address.addressCountry || 'DE',
    };
  }
  if (input.geo) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: input.geo.latitude,
      longitude: input.geo.longitude,
    };
  }
  if (input.aggregateRating && input.aggregateRating.reviewCount > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: input.aggregateRating.ratingValue,
      reviewCount: input.aggregateRating.reviewCount,
    };
  }
  return schema;
};
